<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { TreeNode } from 'primevue/treenode';
import Calendar from 'primevue/calendar';
import OverlayPanel from 'primevue/overlaypanel';
import TreeTable, { TreeTableSelectionKeys, TreeTableExpandedKeys } from 'primevue/treetable';
import RadioButton from 'primevue/radiobutton';
import AppTabMenu from '@/components/AppTabMenu';
import OrganizationContractModal from './components/OrganizationContractModal.vue';
import { useDateTime, can } from '@/composables';
import { useOrganizationContractsStore } from '@/store';
import { ContractDetailInterface, DisciplineInterface } from '@/types';
import { PERMISSIONS } from '@/enum';

const displayModal = ref<boolean>(false);
const selectedContract = ref<ContractDetailInterface>({} as ContractDetailInterface);
const selectedKey = ref<TreeTableSelectionKeys>([]);
const expandedKeys = ref<TreeTableExpandedKeys>([]);
const contractStore = useOrganizationContractsStore();
const { FILTER, scheduleScrub, minScheduleScrubDate, maxScheduleScrubDate, isScrubbed } = storeToRefs(contractStore);
const selectedFilterIndex = ref<number>(0);
const canEdit = contractStore.canEditOrCreateContracts;
const calendar = ref();

const disableCalendarScheduleScrub = computed(() => can([PERMISSIONS.CONTRACTS_SHOW_SCHEDULE_SCRUB_DATE]));
const canEditScheduleScrub = computed(() => {
  return can([PERMISSIONS.CONTRACTS_POSTPONE_SCHEDULE_SCRUB_DATE]) && scheduleScrub.value && !isScrubbed.value;
});

const minDate = computed(() => {
  const minDate = new Date(minScheduleScrubDate.value);
  minDate.setHours(0, 0, 0, 0);

  return minDate;
});

const maxDate = computed(() => {
  const maxDate = new Date(maxScheduleScrubDate.value);
  maxDate.setHours(0, 0, 0, 0);

  return maxDate;
});

const scheduleScrubDate = computed(() => {
  if (!scheduleScrub.value) return '';
  const date = new Date(scheduleScrub.value);

  return date.toLocaleDateString('en-US', { timeZone: 'UTC' });
});

/**
 * The available reports that should be displayed within the tabs.
 */
const filters = Object.values(FILTER.value).map((filter) => ({ label: filter }));

const contracts = ref<TreeNode[]>([]);

onMounted(async () => {
  await getScheduleScrub();
  await getContracts();
});

const getScheduleScrub = async () => {
  await contractStore.getScheduledScrub();
};

const getContracts = async () => {
  await contractStore.getContracts();
  contracts.value = contractStore.contractsTableData;
};

const showContract = async (id: number) => {
  selectedContract.value = contractStore.contracts.find((contract) => contract.id == id) as ContractDetailInterface;

  if (selectedContract.value.id) {
    const id = selectedContract.value.id;
    await contractStore.showContract(id);
    toggleModal();
  }
};

const contractExpansion = async (id: number) => {
  selectedContract.value = contractStore.contracts.find((contract) => contract.id == id) as ContractDetailInterface;
  contractStore.expandOrganizationContract(selectedContract.value.id as number);
  toggleModal();
};

const tabChange = (e: number) => {
  selectedFilterIndex.value = e;
  contractStore.filterContracts(filters[e].label);
  contracts.value = contractStore.contractsTableData;
};

const nameWithComma = (arr: DisciplineInterface[]) =>
  arr.map((item, index) => (index !== arr.length - 1 ? `${item.discipline?.code}, ` : item.discipline?.code)).join('');

const formatModel = (text: string) => {
  return text
    .toLowerCase()
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');
};

const onRenewOrganizationContracts = async () => {
  await contractStore.renewOrganizationContracts(selectedContract.value.id as number);
  toggleModal();
};

const onCreateOrganizationContracts = async () => {
  selectedContract.value = await contractStore.getNewContractTemplate();
  toggleModal();
};

const onSave = async () => {
  toggleModal();
  await getContracts();
  await getScheduleScrub();
  tabChange(selectedFilterIndex.value);
};

const contractIsActive = (contract: ContractDetailInterface) => {
  const today = new Date();
  if (new Date(contract.start_at) < today && new Date(contract.end_at) > today) return true;
  return false;
};

const contractIsFuture = (contract: ContractDetailInterface) => {
  const today = new Date();
  if (new Date(contract.start_at) > today && new Date(contract.end_at) > today) return true;
  return false;
};

const selectionChanged = (selectionObject: TreeTableSelectionKeys) => {
  selectedKey.value = selectionObject;
  const id = Object.keys(selectionObject)[0];
  if (id) {
    selectedContract.value = contractStore.contracts.find(
      (contract) => contract?.id?.toString() == id.toString(),
    ) as ContractDetailInterface;
  }
};

const postponeScheduleScrub = async () => {
  const date = useDateTime().formatDateForApi(scheduleScrub.value);
  await contractStore
    .postponeScheduleScrub(date)
    .then(() => {
      contractStore.doToast('Schedule scrub postponed', 'Success');
    })
    .catch((e) => {
      const message = e.data?.message || 'Failed to postpone schedule scrub';
      contractStore.doToast(message, 'Error', true);
    });

  await getScheduleScrub();
  calendar.value.hide();
};

const viewCalendar = (event: Event) => {
  if (canEditScheduleScrub.value) {
    calendar.value.show(event);
  }
};

const toggleModal = () => {
  displayModal.value = !displayModal.value;
};
</script>

<template>
  <div class="w-full">
    <Card>
      <template #content>
        <OrganizationContractModal v-if="displayModal" :id="selectedContract.id" @cancel="toggleModal" @save="onSave" />
        <div class="flex justify-content-between items-center p-2">
          <div class="col flex items-center">
            <AppTabMenu class-name="filter-menu-container" :items="filters" @tab="tabChange" />
          </div>
          <div v-if="disableCalendarScheduleScrub" class="col-fixed flex items-center">
            <p class="text-dark font-semibold my-auto mr-2">Data Deleted:</p>
            <InputText
              v-model="scheduleScrubDate"
              input-style="width: 20%; margin-left: 5px;"
              :style="{ color: isScrubbed ? 'gray' : 'black' }"
              :disabled="isScrubbed || !scheduleScrub"
              readonly
              @click="viewCalendar"
            />
            <OverlayPanel v-if="canEditScheduleScrub" ref="calendar">
              <Calendar
                v-model="scheduleScrub"
                :min-date="minDate"
                :max-date="maxDate"
                format="MM/dd/yyyy"
                :inline="true"
              >
                <template #footer>
                  <div class="line-class my-auto mr-2 p-2 flex justify-content-end">
                    <AppButton type="secondary" text="Postpone" @click="postponeScheduleScrub" />
                  </div>
                </template>
              </Calendar>
            </OverlayPanel>
          </div>
          <div v-if="canEdit" class="col-fixed flex items-center">
            <AppButton
              v-if="contractStore.contracts.length"
              type="secondary"
              text="Renewal"
              icon-left="add"
              class="ml-2"
              :disabled="!selectedContract?.id"
              @click="onRenewOrganizationContracts"
            />
            <AppButton
              v-else
              type="secondary"
              text="Create New Contract"
              class="ml-2"
              @click="onCreateOrganizationContracts"
            />
          </div>
        </div>

        <TreeTable
          v-if="contracts.length"
          v-model:expanded-keys="expandedKeys"
          :value="contracts"
          data-key="key"
          loading-icon="logo-spinner"
          :loading="contractStore.loading"
          responsive-layout="scroll"
          :selection-mode="canEdit ? 'single' : undefined"
        >
          <template #empty>No contracts found</template>
          <template v-if="contracts.length">
            <Column expander class="w-2rem col-fixed" />
            <Column
              v-if="canEdit"
              body-style="justify-content: center"
              header-class="text-align-center"
              class="col-fixed"
            >
              <template #body="{ node }">
                <RadioButton
                  v-model="selectedKey"
                  :value="{ [node.data.id]: true }"
                  @click="selectionChanged({ [node.data.id]: true })"
                />
              </template>
            </Column>
            <Column field="type" header="Type" header-class="text-align-center">
              <template #body="{ node }">
                <p
                  class="text-primary font-semibold cursor-pointer text-underline"
                  @click="showContract(node.data.id)"
                  v-text="node.data.contract_type.type"
                />
              </template>
            </Column>
            <Column field="start_date" header="Start Date" header-class="text-center" class="pointer-events-none">
              <template #body="{ node }">
                <span class="flex justify-content-center" v-text="useDateTime().formatIntlDate(node.data.start_at)" />
              </template>
            </Column>
            <Column field="end_date" header="End Date" header-class="text-center" class="pointer-events-none">
              <template #body="{ node }">
                <span class="flex justify-content-center" v-text="useDateTime().formatIntlDate(node.data.end_at)" />
              </template>
            </Column>
            <Column field="model" header="Model" header-class="text-center" class="pointer-events-none">
              <template #body="{ node }">
                <span
                  class="flex justify-content-center"
                  v-text="formatModel(node.data.disciplines[0].contract_type)"
                />
              </template>
            </Column>
            <Column field="disciplines" header="Disciplines" header-class="text-center" class="pointer-events-none">
              <template #body="{ node }">
                <span class="flex justify-content-center" v-text="nameWithComma(node.data.disciplines)" />
              </template>
            </Column>
            <Column field="salesrep" header="Sales Rep" header-class="text-center" class="pointer-events-none">
              <template #body="{ node }">
                <span class="flex justify-content-center" v-text="node.data.salesrep?.display_name" />
              </template>
            </Column>

            <Column field="quote_number" header="Quote #" header-class="text-center" class="pointer-events-none">
              <template #body="{ node }">
                <span class="flex justify-content-center" v-text="node.data.quote_number" />
              </template>
            </Column>
            <Column field="prepay" header="Prepay" header-class="text-center" class="pointer-events-none">
              <template #body="{ node }">
                <div class="ml-3">
                  <Icon v-if="node.data.prepay !== 0" class="text-teal-600" name="check" />
                  <Icon v-else class="text-red-600" name="close" />
                </div>
              </template>
            </Column>
            <Column field="actions" header="Actions" header-class="text-center">
              <template #body="{ node }">
                <AppButton
                  v-if="(contractIsActive(node.data) || contractIsFuture(node.data)) && canEdit"
                  class="w-full justify-content-center"
                  :full-width="true"
                  type="text"
                  size="medium"
                  text="Expansion"
                  icon-left="add"
                  @click="contractExpansion(node.data.id)"
                />
                <div v-else class="ml-4">-</div>
              </template>
            </Column>
          </template>
        </TreeTable>
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  padding: 0;
  .p-treetable {
    .p-treetable-thead {
      background: $white;
      padding: 0.5rem;
      tr {
        th {
          color: $darkMedium;
          font-size: 14px;
        }
      }
    }
    .p-treetable-tbody {
      tr {
        td {
          padding: 0.5rem 0.5rem;
        }
      }
    }
  }

  .p-datatable-wrapper {
    margin: 0 !important;
    table {
      table-layout: fixed !important;
      width: 100%;
    }
  }

  .filter-menu-container {
    .p-tabmenu-nav {
      border: none;

      .p-tabmenuitem {
        border: 2px solid #f1f3f3;
        border-right: 1px solid #f1f3f3;
        padding: 0;
        align-items: center;
        justify-content: center;
        display: flex;
        width: 96px;
        height: 32px;

        .p-menuitem-link {
          width: 100%;
          text-align: center;
          justify-content: center;
          background: none;
          border: none;
          font-weight: bold;
          font-size: 14px;
          line-height: 18px;
          color: #3b4242;
          padding: 0;
          margin: 0;
          .p-menuitem-text {
            line-height: 2 !important;
          }

          &:hover {
            text-decoration: none;
          }
        }

        &.p-highlight {
          background-color: #c4e8e8;
          border-color: #c4e8e8;
        }
      }

      .p-tabmenuitem:first-of-type {
        border-radius: 4px 0px 0px 4px;
      }

      .p-tabmenuitem:nth-last-of-type(2) {
        border-right: 2px solid #f1f3f3;
        border-radius: 0px 4px 4px 0px;
      }
    }
  }
}

.text-red-600 {
  color: $red !important;
}

.line-class {
  border-top: 2px solid #ccc;
}
</style>
